import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Plus } from 'lucide-react';
import { ROLE_TEMPLATES, ROLE_TYPE_LABELS, PERMISSION_LABELS } from '@/types/peo-organization.types';
import type { PEORoleType, PermissionLevel } from '@/types/peo-organization.types';

interface CreateRoleDialogProps {
  providerId: string;
  children: React.ReactNode;
}

export const CreateRoleDialog: React.FC<CreateRoleDialogProps> = ({
  providerId,
  children
}) => {
  const [open, setOpen] = useState(false);
  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [roleType, setRoleType] = useState<PEORoleType | ''>('');
  const [permissionLevel, setPermissionLevel] = useState<PermissionLevel>('own_only');
  const [permissions, setPermissions] = useState({
    can_view_applications: true,
    can_create_applications: false,
    can_edit_applications: true,
    can_delete_applications: false,
    can_view_quotes: true,
    can_create_quotes: true,
    can_edit_quotes: true,
    can_delete_quotes: false,
    can_view_analytics: false,
    can_manage_team: false,
    can_manage_roles: false
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Create role mutation
  const createRole = useMutation({
    mutationFn: async () => {
      if (!roleType) throw new Error('Role type is required');

      const { error } = await supabase
        .from('peo_organizational_roles')
        .insert({
          provider_id: providerId,
          role_name: roleName,
          description: description || null,
          role_type: roleType,
          permission_level: permissionLevel,
          ...permissions
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizational-roles', providerId] });
      queryClient.invalidateQueries({ queryKey: ['organization-stats', providerId] });
      toast({
        title: "Role Created",
        description: "New organizational role has been created successfully."
      });
      handleClose();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create role.",
        variant: "destructive"
      });
    }
  });

  const handleRoleTypeChange = (newRoleType: PEORoleType) => {
    setRoleType(newRoleType);
    // Apply template defaults
    const template = ROLE_TEMPLATES[newRoleType];
    if (template) {
      setPermissions(prev => ({
        ...prev,
        ...template
      }));
    }
  };

  const handleClose = () => {
    setOpen(false);
    setRoleName('');
    setDescription('');
    setRoleType('');
    setPermissionLevel('own_only');
    setPermissions({
      can_view_applications: true,
      can_create_applications: false,
      can_edit_applications: true,
      can_delete_applications: false,
      can_view_quotes: true,
      can_create_quotes: true,
      can_edit_quotes: true,
      can_delete_quotes: false,
      can_view_analytics: false,
      can_manage_team: false,
      can_manage_roles: false
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create New Role
          </DialogTitle>
          <DialogDescription>
            Define a new organizational role with specific permissions and access levels.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="roleName">Role Name *</Label>
              <Input
                id="roleName"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="e.g., Senior Sales Manager"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of the role and responsibilities..."
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="roleType">Role Type *</Label>
              <Select value={roleType} onValueChange={handleRoleTypeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role type..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="permissionLevel">Permission Level</Label>
              <Select value={permissionLevel} onValueChange={(value: PermissionLevel) => setPermissionLevel(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PERMISSION_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Permissions */}
          <div className="space-y-4">
            <h4 className="font-medium">Permissions</h4>
            
            <div className="grid grid-cols-1 gap-4">
              {/* Application Permissions */}
              <div className="space-y-3">
                <h5 className="text-sm font-medium text-muted-foreground">Application Management</h5>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center justify-between space-x-2">
                    <Label htmlFor="can_view_applications" className="text-sm">View Applications</Label>
                    <Switch
                      id="can_view_applications"
                      checked={permissions.can_view_applications}
                      onCheckedChange={(checked) => setPermissions(prev => ({ ...prev, can_view_applications: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between space-x-2">
                    <Label htmlFor="can_create_applications" className="text-sm">Create Applications</Label>
                    <Switch
                      id="can_create_applications"
                      checked={permissions.can_create_applications}
                      onCheckedChange={(checked) => setPermissions(prev => ({ ...prev, can_create_applications: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between space-x-2">
                    <Label htmlFor="can_edit_applications" className="text-sm">Edit Applications</Label>
                    <Switch
                      id="can_edit_applications"
                      checked={permissions.can_edit_applications}
                      onCheckedChange={(checked) => setPermissions(prev => ({ ...prev, can_edit_applications: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between space-x-2">
                    <Label htmlFor="can_delete_applications" className="text-sm">Delete Applications</Label>
                    <Switch
                      id="can_delete_applications"
                      checked={permissions.can_delete_applications}
                      onCheckedChange={(checked) => setPermissions(prev => ({ ...prev, can_delete_applications: checked }))}
                    />
                  </div>
                </div>
              </div>

              {/* Quote Permissions */}
              <div className="space-y-3">
                <h5 className="text-sm font-medium text-muted-foreground">Quote Management</h5>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center justify-between space-x-2">
                    <Label htmlFor="can_view_quotes" className="text-sm">View Quotes</Label>
                    <Switch
                      id="can_view_quotes"
                      checked={permissions.can_view_quotes}
                      onCheckedChange={(checked) => setPermissions(prev => ({ ...prev, can_view_quotes: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between space-x-2">
                    <Label htmlFor="can_create_quotes" className="text-sm">Create Quotes</Label>
                    <Switch
                      id="can_create_quotes"
                      checked={permissions.can_create_quotes}
                      onCheckedChange={(checked) => setPermissions(prev => ({ ...prev, can_create_quotes: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between space-x-2">
                    <Label htmlFor="can_edit_quotes" className="text-sm">Edit Quotes</Label>
                    <Switch
                      id="can_edit_quotes"
                      checked={permissions.can_edit_quotes}
                      onCheckedChange={(checked) => setPermissions(prev => ({ ...prev, can_edit_quotes: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between space-x-2">
                    <Label htmlFor="can_delete_quotes" className="text-sm">Delete Quotes</Label>
                    <Switch
                      id="can_delete_quotes"
                      checked={permissions.can_delete_quotes}
                      onCheckedChange={(checked) => setPermissions(prev => ({ ...prev, can_delete_quotes: checked }))}
                    />
                  </div>
                </div>
              </div>

              {/* Administrative Permissions */}
              <div className="space-y-3">
                <h5 className="text-sm font-medium text-muted-foreground">Administrative</h5>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center justify-between space-x-2">
                    <Label htmlFor="can_view_analytics" className="text-sm">View Analytics</Label>
                    <Switch
                      id="can_view_analytics"
                      checked={permissions.can_view_analytics}
                      onCheckedChange={(checked) => setPermissions(prev => ({ ...prev, can_view_analytics: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between space-x-2">
                    <Label htmlFor="can_manage_team" className="text-sm">Manage Team</Label>
                    <Switch
                      id="can_manage_team"
                      checked={permissions.can_manage_team}
                      onCheckedChange={(checked) => setPermissions(prev => ({ ...prev, can_manage_team: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between space-x-2">
                    <Label htmlFor="can_manage_roles" className="text-sm">Manage Roles</Label>
                    <Switch
                      id="can_manage_roles"
                      checked={permissions.can_manage_roles}
                      onCheckedChange={(checked) => setPermissions(prev => ({ ...prev, can_manage_roles: checked }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={() => createRole.mutate()}
            disabled={!roleName.trim() || !roleType || createRole.isPending}
          >
            {createRole.isPending ? 'Creating...' : 'Create Role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};